import React, { useEffect, useRef, useState } from 'react';
import { GetAccountQueueById } from '../../../services/backendServices';
import { Button } from 'react-bootstrap';
import { useAccountContext } from '../../context/AccountContext';
import { ACCOUNT_QUEUE_STATUS } from '../../../utils/key_helper';
import { useProfileContext } from '../../context/ProfileContext';
import { CancelAccountQueue, DeleteAccountQueue } from '../../../services/backendServices';
import { getTruncatedText, WithTooltip } from '../../user-pages/ui-helper';
import toast from 'react-hot-toast';

{/*
 * AccountQueueCard component displays a list of account queue items with their status.
 * It allows users to cancel or delete queue items based on their status.
 * The component uses polling to update the status of each queue item every 2 seconds.
 * It also manages progress bars for items that are currently processing.
 * It is aloso allowed to cancel or delete the queue item based on the status of the queue item.
 * The component uses the AccountContext to manage the queue data and the ProfileContext to refetch profile data.
 */}
const AccountQueueCard = () => {
    const { accountQueueData, updateQueueStatus, removeQueueData, setIsAccountDataFetched } = useAccountContext();
    const queueRef = useRef(accountQueueData);
    const currentPath = window.location.pathname.toLowerCase();
    const [progressMap, setProgressMap] = useState({});
    const { setRefetchProfileData } = useProfileContext();
    const [isLoading, setIsLoading] = useState({ cancel_account_queue: false, delete_account_queue: false });
    // Track completed queue items to avoid multiple API calls
    const [completedQueueIds, setCompletedQueueIds] = useState(new Set());
    // This function is used to cancel the account queue
    const CancelAccountQueueApiCall = async (Values) => {
        setIsLoading((prevState) => ({ ...prevState, cancel_account_queue: true }));
        try {
            const res = await CancelAccountQueue(Values);
            if (res.status) {
                // fetch profile data to showcase updated wallet amount
                setRefetchProfileData(true);
                // remove the queue data from the state
                removeQueueData(Values.queue_id);
            } else {
                toast.error(res.msg);
            }
        } catch (e) {
            console.error("Error Cancel  Account Queue", e);
        } finally {
            setIsLoading((prevState) => ({ ...prevState, cancel_account_queue: false }));
        }
    };
    // This function is used to delete the account queue
    const DeleteAccountQueueApiCall = async (Values) => {
        setIsLoading((prevState) => ({ ...prevState, delete_account_queue: true }));
        try {
            const res = await DeleteAccountQueue(Values);
            if (res.status) {
                // remove the queue data from the state
                removeQueueData(Values.queue_id);
            } else {
                toast.error(res.msg);
            }
        } catch (e) {
            console.error("Error Delete  Account Queue", e);
        } finally {
            setIsLoading((prevState) => ({ ...prevState, delete_account_queue: false }));
        }
    };
    // Keep queueRef updated
    useEffect(() => {
        queueRef.current = accountQueueData;

    }, [accountQueueData]);
    // Polling to update queue status
    // This will check the status of each queue item every 2 seconds
    useEffect(() => {
        const poll = () => {
            accountQueueData.forEach(async (item) => {
                // Only poll for items that are CONNECTING or RENEWING
                if (
                    item.status === ACCOUNT_QUEUE_STATUS.CONNECTING ||
                    item.status === ACCOUNT_QUEUE_STATUS.RENEWING
                ) {
                    try {
                        const response = await GetAccountQueueById({ queue_id: item.id });
                        const newStatus = response?.data?.status;
                        const newRemark = response?.data?.remark;
                        // Update the queue status if it has changed
                        // This will ensure that the UI is updated with the latest status of the queue items
                        if (newStatus && newStatus !== item.status) {
                            updateQueueStatus(item.id, newStatus, newRemark);
                        }
                    } catch (error) {
                        console.error('Error polling status:', error);
                    }
                }
            });
        };
        const interval = setInterval(poll, 2000); // every 2 seconds
        return () => clearInterval(interval);
    }, [accountQueueData]);


    useEffect(() => {
        const poll = async () => {
            for (const item of accountQueueData) {
                // Only poll for items that are still processing
                if (
                    item.status === ACCOUNT_QUEUE_STATUS.CONNECTING ||
                    item.status === ACCOUNT_QUEUE_STATUS.RENEWING
                ) {
                    try {
                        const response = await GetAccountQueueById({ queue_id: item.id });
                        const newStatus = response?.data?.status;
                        const newRemark = response?.data?.remark;

                        if (newStatus && newStatus !== item.status) {
                            updateQueueStatus(item.id, newStatus, newRemark);
                        }

                        // Handle completed status only once
                        if (
                            newStatus === ACCOUNT_QUEUE_STATUS.COMPLETED &&
                            !completedQueueIds.has(item.id)
                        ) {
                            setCompletedQueueIds((prev) => new Set(prev).add(item.id));

                            // Update progress bar
                            setProgressMap((prev) => ({ ...prev, [item.id]: 100 }));

                            // Fetch account data after a short delay
                            setTimeout(() => {
                                setIsAccountDataFetched(false)
                                setRefetchProfileData(true);
                                removeQueueData(item.id);
                            }, 2000);
                        }

                        // Handle failed items
                        if (newStatus === ACCOUNT_QUEUE_STATUS.FAILED) {
                            setRefetchProfileData(true);
                        }
                    } catch (error) {
                        console.error('Error polling status:', error);
                    }
                }
            }
        };

        const interval = setInterval(poll, 2000);
        return () => clearInterval(interval);
    }, [accountQueueData, completedQueueIds]);
    // Helper functions to get icon class, subtext, and color based on status
    const getIconClass = (status) => {
        switch (status) {
            case ACCOUNT_QUEUE_STATUS.FAILED:
                return 'mdi mdi-alert-circle-outline';
            case ACCOUNT_QUEUE_STATUS.COMPLETED:
                return 'mdi mdi-check-circle-outline';
            default:
                return 'mdi mdi-loading mdi-spin';
        }
    };
    const getSubText = (status, remark) => {
        switch (status) {
            case ACCOUNT_QUEUE_STATUS.FAILED:
                return remark;
            case ACCOUNT_QUEUE_STATUS.COMPLETED:
                return remark;
            default:
                return null;
        }
    };
    const getColor = (status) => {
        switch (status) {
            case ACCOUNT_QUEUE_STATUS.FAILED:
                return 'var(--error)';
            case ACCOUNT_QUEUE_STATUS.COMPLETED:
                return 'var(--color-primary)';
            default:
                return 'var(--color-primary)';
        }
    };

    return (
        <div className={`${'toast-container'} `}>
            {/* Render each account queue item as a toast card */}
            {accountQueueData?.map((item) => {
                const subText = getSubText(item.status, item.remark);
                // Determine if the item is currently progressing
                // This will be used to show a spinner or a progress bar
                // If the status is CONNECTING or RENEWING, we show a spinner
                // If the status is COMPLETED, we show a progress bar that jumps to 100%
                const isProgressing =
                    item.status === ACCOUNT_QUEUE_STATUS.CONNECTING ||
                    item.status === ACCOUNT_QUEUE_STATUS.RENEWING;
                return (
                    <div key={item.id} className="toast-card mb-3">
                        <div className="toast-icon-section">
                            {/* Show a spinner if the item is progressing, otherwise show an icon */}
                            {/* The spinner is shown for CONNECTING and RENEWING statuses */}
                            {isProgressing ? (
                                <div className="progress-spinner" />
                            ) : (
                                <i
                                    className={`toast-icon ${getIconClass(item.status)}`}
                                    style={{ color: getColor(item.status) }}
                                />
                            )}
                        </div>
                        <div className="toast-content">
                            <div className="toast-header">
                                <div>
                                    {/* Show the account name or broker combine name */}
                                    <div className="toast-text">
                                        {getTruncatedText(item.broker_combine_name, 30, "mb-0", "h5")}
                                    </div>
                                </div>
                                {/* shows the Button to cancel or delete the queue item */}
                                {/* If the item is progressing , we show a cancel button and if failed than delete button */}
                                {(isProgressing || item.status === ACCOUNT_QUEUE_STATUS.FAILED) && (
                                    <WithTooltip tooltipText={isProgressing ? 'Cancel' : 'Remove'}>
                                        <Button
                                            variant=''
                                            style={{ cursor: 'pointer', zIndex: 9999999 }}
                                            className="toast-close p-1 ms-3 "
                                            disabled={isLoading.cancel_account_queue || isLoading.delete_account_queue}
                                            onClick={() => {
                                                if (isProgressing) {
                                                    CancelAccountQueueApiCall({ queue_id: item.id });
                                                } else {
                                                    DeleteAccountQueueApiCall({ queue_id: item.id });
                                                }
                                            }}
                                        >
                                            {isProgressing ? 'Cancel' : <i className="mdi mdi-close fs-5" />}
                                        </Button>
                                    </WithTooltip>
                                )}

                            </div>
                            {subText && <div className="toast-subtext">{subText}</div>}
                            {/* Show a progress bar if the item is progressing */}
                            {isProgressing && (
                                <div className="toast-progress-bar">
                                    <div className="toast-progress" style={{ width: `${progressMap[item.id] || 0}%` }} />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AccountQueueCard;
